import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { clone } from "@formant/core/common/clone";
import { defined } from "@formant/core/common/defined";
import { ICustomModuleParameters } from "@formant/core/model/ICustomModuleParameters";
import { IDevice } from "@formant/core/model/IDevice";
import { IFilter } from "@formant/core/model/IFilter";
import { IFullScreenLayout } from "@formant/core/model/IFullScreenLayout";
import { ILayoutModule } from "@formant/core/model/ILayoutModule";
import { IView } from "@formant/core/model/IView";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { authStore, customModuleStore } from "../../stores";
import { createCustomWebViewUrl } from "../../utils/createCustomWebViewUrl";
import { CustomModule } from "../CustomModule";
import * as styles from "./FullscreenLayout.scss";

export interface IFullscreenLayoutProps {
    view: IView;
    context: IFilter;
    device?: IDevice;
    layout: IFullScreenLayout;
    onSaveLayout: (layout: IFullScreenLayout) => void;
}

@observer
export class FullscreenLayout extends Component<IFullscreenLayoutProps> {
    @observable
    private authToken?: string = undefined;

    public componentDidMount() {
        authStore
            .token()
            .then(_ => (this.authToken = _))
            .catch(err => console.error(err));
    }

    render() {
        const { authToken, onConfigurationChanged } = this;
        const { view, device } = this.props;

        const id = device?.id;
        const modules = customModuleStore.listAll();
        let moduleId: string | undefined;
        let viewConfigurationId: string | undefined;
        if (view) {
            const backgroundModule = view.layout.backgroundModule as
                | ILayoutModule
                | undefined;
            if (backgroundModule) {
                moduleId = (
                    backgroundModule.configuration?.parameters as
                        | ICustomModuleParameters
                        | undefined
                )?.customModuleDefinitionId;
                viewConfigurationId = (
                    backgroundModule.configuration?.parameters as
                        | ICustomModuleParameters
                        | undefined
                )?.customModuleConfigurationId;
            }
        }
        const module = modules?.find(_ => _.id === moduleId);
        const loadingModules = moduleId && !module;
        const fullscreenUrl = module?.url || view.url;
        if (loadingModules || !fullscreenUrl || !authToken) {
            return <LoadingIndicator visible={true} />;
        }

        return (
            <CustomModule
                moduleSchemaUrl={module?.configurationSchemaUrl ?? undefined}
                moduleConfigurationId={viewConfigurationId ?? undefined}
                streamName={"view:" + view.id}
                src={createCustomWebViewUrl(
                    fullscreenUrl,
                    module?.url !== undefined,
                    id,
                    authToken,
                    undefined,
                    undefined,
                    "view:" + defined(view.id),
                    viewConfigurationId ?? undefined,
                )}
                className={styles["web-view"]}
                onConfigurationChanged={onConfigurationChanged}
            />
        );
    }

    onConfigurationChanged = async (configurationContent: string) => {
        const { view, onSaveLayout } = this.props;
        const backgroundModule = view.layout.backgroundModule as
            | ILayoutModule
            | undefined;
        const params = defined(
            backgroundModule?.configuration?.parameters,
        ) as ICustomModuleParameters;
        const configId = params.customModuleConfigurationId;
        if (configId) {
            await customModuleStore.updateConfiguration(configId, {
                configuration: configurationContent,
            });
        } else {
            const newConfig = await customModuleStore.createConfiguration({
                configuration: configurationContent,
            });
            const newLayout = clone(view.layout) as IFullScreenLayout;
            const newParams = defined(
                newLayout.backgroundModule?.configuration?.parameters,
            ) as ICustomModuleParameters;
            newParams.customModuleConfigurationId = newConfig.id;
            onSaveLayout(newLayout);
        }
    };
}
